import React from "react";
import { Theme, createStyles, withStyles, Typography, fade, Button, MenuItem, TextField } from "@material-ui/core";

import { inject, observer } from "mobx-react";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import { MeetingSnapshot } from "../../domain/api/meetings/MeetingSnapshot";
import UserAvatar from "../shared/UserAvatar";
import moment from "moment";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import i18n from "../../services/localization/i18n";
import AppIcon from "../shared/AppIcon";
import { AppIconType } from "../shared/AppIconType";
import { PredefinedSnapshotType } from "../../domain/api/meetings/PredefinedSnapshotType";
import meetingSnapshotDataService from "../../services/api/MeetingSnapshotDataService";
import { MeetingSnapshotSentiment } from "../../domain/api/meetings/MeetingSnapshotSentiment";

const styles = (theme: Theme) => createStyles({
    actions: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        height: theme.spacing(7)
    },
    button: {
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(),
        marginTop: theme.spacing(),
    },
    header: {
        display: "flex",
        flexDirection: "row", 
        flexGrow: 1,
    },
    rating: {
        display: "flex",
        flexDirection: "row",
    },
    root: {
        "&:hover": {
            backgroundColor: theme.palette.background.default,
        },
        "backgroundColor": fade(theme.palette.background.default, 0.9),
        borderColor: theme.palette.secondary.main,
        borderStyle: "solid",
        borderWidth: "1",
        color: theme.palette.text.primary,
        width: theme.spacing(50),
        position: "absolute",
        top: theme.spacing(13),
        right: theme.spacing(),
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding: theme.spacing()
    },

    title:  {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        justifyContent: "center",
        marginTop: theme.spacing(1.5),
    },
    selector: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        margin: theme.spacing(),
        marginTop: theme.spacing(),
        paddingRight: theme.spacing(),
    },
    selectorIcon: {
        marginRight: theme.spacing()
    }
});


interface ISnapshotCardProps {
    classes: {
        actions: string;
        button: string;
        header: string;
        rating: string;
        root: string;
        selector: string;
        selectorIcon: string;
        title: string;
    };
    snapshot: MeetingSnapshot;
    onGoLive: () => void;
}

interface ISnapshotCardState {
    note: string;
    sentiment?: MeetingSnapshotSentiment;
    sentimentValue: SentimentType;
    title: string;
    type: PredefinedSnapshotType;
}

declare type SentimentType = 'positive' | 'negative' | '' ;

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.CONFIG)
@observer
class SnapshotCard extends React.Component<ISnapshotCardProps & IContextStoreProps & IConfigStoreProps, ISnapshotCardState> {

    /**
     *Creates an instance of SnapshotCard.
     * @param {(ISnapshotCardProps & IContextStoreProps)} props
     * @memberof SnapshotCard
     */
    constructor(props: ISnapshotCardProps & IContextStoreProps & IConfigStoreProps) {
        super(props);
        
        const { me } = props.configStore!;
        const sentiment = props.snapshot.sentiments.find(s => s.userId === me!.id!);

        let sentimentValue: SentimentType  = '';
        let sentimentNote: string = '';

        if (sentiment) {
            sentimentNote = sentiment.note ? sentiment.note : '';

            if (sentiment?.sentiment) {
                if (sentiment?.sentiment < 50) {
                    sentimentValue = 'negative';
                } else {
                    sentimentValue = 'positive';
                }
            }
        }

        this.state = {
            sentiment,
            sentimentValue:  sentimentValue,
            note: sentimentNote,
            title: props.snapshot.title,
            type: props.snapshot.snapshotType,
        }
    }

    public render() {

        const { snapshot } = this.props;
        const { header, root, selector, selectorIcon, title } = this.props.classes;
        const { snapshotUsers } =  this.props.contextStore!;

        const user = snapshotUsers.find(p => p.userId === snapshot.userId);
        if (!user) {
            
            throw new Error("No user with id:" + snapshot.userId + " found in snapshot users");
        }

        const longTime = moment(snapshot.timestamp).format("MMM Do HH:mm:ss");
        const shortTime = moment(snapshot.timestamp).format("MMM Do HH:mm:ss");

        return <div className={root}>
            
            { this.renderCta() }

            <div className={header}>
                <UserAvatar user={user} />
                
                <div className={title}>
                    <Typography variant="subtitle1">
                        { user.displayName}
                    </Typography>
                    <Typography variant="caption" title={longTime}>
                        { shortTime }
                    </Typography>
                </div>
            </div>
            <TextField
                className={selector}
                fullWidth={true}
                label="Title"
                variant="outlined"
                value={this.state.title}
                onChange={e => 
                    this.setState({
                        title: e.target.value        
                    })} 
                onBlur={this.onSnapshotChange}
            />
            <TextField
                fullWidth={true}
                className={selector}
                select={true}
                variant="outlined"
                label="Visibilty"
                value={this.state.type}
                onChange={this.onSnapshotTypeChange}>
                    <MenuItem key="personal" value={PredefinedSnapshotType.PERSONAL}>
                        <AppIcon icon={AppIconType.VisibilityOff}  className={selectorIcon} />
                        { i18n.t("snapshot_personal_value") }
                    </MenuItem>
                    <MenuItem key="team" value={PredefinedSnapshotType.TEAM}>
                        <AppIcon icon={AppIconType.GroupWork}  className={selectorIcon} />
                        { i18n.t("snapshot_team_value") }
                    </MenuItem>
                    <MenuItem key="public" value={PredefinedSnapshotType.ALL_PARTICIPANTS}>
                        <AppIcon icon={AppIconType.VisibilityOn}  className={selectorIcon} />
                        { i18n.t("snapshot_public_value") }
                    </MenuItem>                                        
            </TextField>        
            <TextField
                fullWidth={true}
                className={selector}
                variant="outlined"
                select={true}
                label="Sentiment"
                placeholder="Is snapshot positive?"
                value={this.state.sentimentValue}
                onChange={this.onSentimentValueChanged}>
                    <MenuItem key="personal" value="positive">
                        <AppIcon icon={AppIconType.ThumbUp} className={selectorIcon} />
                        { i18n.t("snapshot_sentiment_positive") }
                    </MenuItem>
                    <MenuItem key="team" value="negative">
                        <AppIcon icon={AppIconType.ThumbDown} className={selectorIcon} />
                        { i18n.t("snapshot_sentiment_negative") }
                    </MenuItem>
            </TextField>  
            <TextField
                className={selector}
                fullWidth={true}
                label="Title"
                variant="outlined"
                multiline={true}
                rows={3}
                value={this.state.note}
                onChange={e => 
                    this.setState({
                        note: e.target.value        
                    })} 
                onBlur={this.onSentimentChanged}
                />

        </div>
    }

    
    private renderCta = () => {
        const { classes }  = this.props;
        const { finishedMeet  } = this.props.contextStore!;
        return <Button variant="contained" 
                        className={classes.button}
                        color="secondary"
                        onClick={this.props.onGoLive}>
            <AppIcon icon={AppIconType.Back} style={{ marginRight: 8}} />
            
            { finishedMeet ? i18n.t("snapshots_final_back"): i18n.t("snapshots_live_text") }
        </Button>
    }

    private onSnapshotTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            type: parseInt(event.target.value) as PredefinedSnapshotType
        }, this.onSnapshotChange);
    }

    private onSnapshotChange = () => {
        const { snapshot } = this.props;
        const { addOrUpdateSnapshot, setLoadedSnapshot } = this.props.contextStore!;

        if (snapshot.title === this.state.title &&
            snapshot.snapshotType === this.state.type) {
            return;
        }
                
        const updatedSnapshot = {...snapshot};
        updatedSnapshot.title = this.state.title;
        updatedSnapshot.snapshotType = this.state.type;

        meetingSnapshotDataService.updateSnapshotAsync(updatedSnapshot)
            .then(snapshot => {
                addOrUpdateSnapshot(snapshot);
                setLoadedSnapshot(snapshot);
            });
    }

    private onSentimentValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            sentimentValue: event.target.value === 'positive' ? 'positive' : 'negative'
        }, this.onSentimentChanged);
    }
    
    private onSentimentChanged = () => {
        const { id, partitionKey } = this.props.snapshot;
        const { me } = this.props.configStore!;
        const { addOrUpdateSnapshot } = this.props.contextStore!;

        const newSentimentValue = this.state.sentimentValue === 'positive' ? 100 : 1;

        if (this.state.sentiment?.note === this.state.note &&
            this.state.sentiment?.sentiment === newSentimentValue) {
            return;
        }

        const sentiment = {
            userId: me!.id,
            partitionKey, 
            sentiment: newSentimentValue,
            note: this.state.note
        } as MeetingSnapshotSentiment;

        meetingSnapshotDataService.recordSentimentAsync(id!, sentiment)
            .then(snapshot => {
                addOrUpdateSnapshot(snapshot);
            });
    }
}

export default withStyles(styles)(SnapshotCard);

